import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../home/components/Navbar';

export default function ShopPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const shopCategories = [
    {
      id: 'chips',
      title: 'Chips Store',
      titleCn: 'Chips Store',
      description: 'Purchase chips with exclusive website discounts',
      descriptionCn: 'Purchase chips with exclusive website discounts',
      gradient: 'from-amber-500 to-orange-600',
      path: '/shop/chips',
      badge: 'Up to 284% Bonus',
      badgeCn: 'Up to 284% Bonus',
      highlight: '#F59E0B',
      features: ['8 Packages Available', 'Up to 284% Bonus'],
    },
    {
      id: 'coins',
      title: 'Gold Coins Store',
      titleCn: 'Gold Coins Store',
      description: 'Get gold coins with better deals than in-game',
      descriptionCn: 'Get gold coins with better deals than in-game',
      gradient: 'from-yellow-400 to-amber-500',
      path: '/shop/coins',
      badge: 'Up to 260% Bonus',
      badgeCn: 'Up to 260% Bonus',
      highlight: '#EAB308',
      features: ['6 Packages Available', 'Up to 260% Bonus'],
    },
  ];

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <Navbar isScrolled={isScrolled} />

      {/* Hero Banner with Background Image */}
      <section className="relative pt-0 pb-0 overflow-hidden" style={{ minHeight: '520px' }}>
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=luxury%20casino%20poker%20table%20with%20golden%20chips%20stacked%20high%2C%20dramatic%20cinematic%20lighting%2C%20dark%20rich%20atmosphere%2C%20deep%20green%20felt%20table%2C%20glowing%20amber%20and%20gold%20tones%2C%20premium%20gambling%20environment%2C%20wide%20angle%20shot%2C%20ultra%20detailed%20photorealistic%20render&width=1440&height=520&seq=shop-hero-bg-001&orientation=landscape"
            alt="Shop Hero"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-zinc-950"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-black/30"></div>
        </div>

        {/* Decorative glows */}
        <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl -translate-y-1/2 pointer-events-none"></div>
        <div className="absolute top-1/2 right-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl -translate-y-1/2 pointer-events-none"></div>

        <div className="relative max-w-5xl mx-auto text-center px-4 pt-44 pb-24">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 border border-amber-500/40 rounded-full mb-6 backdrop-blur-sm">
            <i className="ri-vip-crown-fill text-amber-400 text-base"></i>
            <span className="text-sm font-semibold text-amber-300 tracking-wide">Official Website Exclusive</span>
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black mb-6 leading-tight">
            <span className="bg-gradient-to-r from-amber-300 via-yellow-400 to-orange-400 bg-clip-text text-transparent drop-shadow-lg">
              Premium Shop
            </span>
          </h1>

          <p className="text-xl text-zinc-200 mb-3 max-w-2xl mx-auto font-light">
            Exclusive website offers — get <strong className="text-amber-400 font-bold">more bonus</strong> than in-game purchases
          </p>
          <p className="text-base text-zinc-400 max-w-xl mx-auto">
            Exclusive website discounts, get more rewards than in-game purchases
          </p>
        </div>
      </section>

      {/* Shop Categories */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold text-white mb-2">Choose Your Store</h2>
            <p className="text-zinc-400 text-sm">Select the store you need</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {shopCategories.map((category) => (
              <div
                key={category.id}
                className="group relative overflow-hidden bg-zinc-900 border border-zinc-800 rounded-3xl hover:border-amber-500/50 transition-all duration-500 cursor-pointer hover:shadow-2xl hover:shadow-amber-500/10 hover:-translate-y-1"
                onClick={() => navigate(category.path)}
              >
                {/* Top gradient bar */}
                <div className={`h-1.5 w-full bg-gradient-to-r ${category.gradient}`}></div>

                {/* Badge */}
                <div className="absolute top-6 right-6 px-3 py-1.5 bg-gradient-to-r from-amber-500/25 to-orange-500/25 border border-amber-500/40 rounded-full backdrop-blur-sm">
                  <span className="text-xs font-bold text-amber-300">{category.badge}</span>
                </div>

                <div className="p-8 pt-7">
                  {/* Icon area */}
                  <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${category.gradient} flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg`}>
                    {category.id === 'chips' ? (
                      <svg width="40" height="40" viewBox="0 0 64 64" fill="none">
                        <circle cx="32" cy="32" r="28" fill="url(#pg1)" stroke="#FCD34D" strokeWidth="2"/>
                        <circle cx="32" cy="32" r="20" fill="url(#pg2)" stroke="#FDE68A" strokeWidth="1.5"/>
                        <rect x="30" y="4" width="4" height="7" rx="2" fill="#FCD34D"/>
                        <rect x="30" y="53" width="4" height="7" rx="2" fill="#FCD34D"/>
                        <rect x="4" y="30" width="7" height="4" rx="2" fill="#FCD34D"/>
                        <rect x="53" y="30" width="7" height="4" rx="2" fill="#FCD34D"/>
                        <text x="32" y="37" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#FEF3C7" fontFamily="Arial">M</text>
                        <defs>
                          <radialGradient id="pg1" cx="40%" cy="35%" r="60%"><stop offset="0%" stopColor="#D97706"/><stop offset="100%" stopColor="#78350F"/></radialGradient>
                          <radialGradient id="pg2" cx="40%" cy="35%" r="60%"><stop offset="0%" stopColor="#F59E0B"/><stop offset="100%" stopColor="#B45309"/></radialGradient>
                        </defs>
                      </svg>
                    ) : (
                      <svg width="40" height="40" viewBox="0 0 64 64" fill="none">
                        <circle cx="32" cy="32" r="28" fill="url(#pg3)" stroke="#FCD34D" strokeWidth="2"/>
                        <circle cx="32" cy="32" r="20" fill="none" stroke="#FDE68A" strokeWidth="1" strokeDasharray="3 2"/>
                        <ellipse cx="26" cy="24" rx="7" ry="4" fill="#FEF3C7" opacity="0.4" transform="rotate(-20 26 24)"/>
                        <text x="32" y="40" textAnchor="middle" fontSize="20" fontWeight="900" fill="#78350F" fontFamily="Georgia,serif">$</text>
                        <defs>
                          <radialGradient id="pg3" cx="35%" cy="30%" r="65%"><stop offset="0%" stopColor="#FDE68A"/><stop offset="40%" stopColor="#F59E0B"/><stop offset="100%" stopColor="#B45309"/></radialGradient>
                        </defs>
                      </svg>
                    )}
                  </div>

                  {/* Content */}
                  <h2 className="text-2xl font-bold mb-1 text-white group-hover:text-amber-400 transition-colors">
                    {category.title}
                  </h2>
                  <p className="text-sm text-zinc-500 mb-5">{category.titleCn}</p>

                  {/* Feature tags */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {category.features.map((f) => (
                      <span key={f} className="px-2.5 py-1 bg-zinc-800 border border-zinc-700 rounded-full text-xs text-zinc-300">
                        {f}
                      </span>
                    ))}
                  </div>

                  {/* CTA Button */}
                  <button className={`w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-gradient-to-r ${category.gradient} rounded-xl font-bold text-white group-hover:shadow-xl group-hover:shadow-amber-500/30 transition-all duration-300 whitespace-nowrap text-base`}>
                    <span>Browse Offers</span>
                    <i className="ri-arrow-right-line text-lg group-hover:translate-x-1 transition-transform"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 px-4 bg-zinc-900/40 border-t border-zinc-800/50">
        <div className="max-w-5xl mx-auto">
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { icon: 'ri-shield-check-line', title: 'Secure Payment', sub: 'Secure Payment Protection', color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20' },
              { icon: 'ri-flashlight-line', title: 'Instant Delivery', sub: 'Instant Delivery', color: 'text-orange-400', bg: 'bg-orange-500/10 border-orange-500/20' },
              { icon: 'ri-customer-service-2-line', title: '24/7 Support', sub: '24/7 Customer Support', color: 'text-yellow-400', bg: 'bg-yellow-500/10 border-yellow-500/20' },
            ].map((f) => (
              <div key={f.title} className={`text-center p-7 rounded-2xl border ${f.bg}`}>
                <div className={`w-14 h-14 rounded-full ${f.bg} border flex items-center justify-center mx-auto mb-4`}>
                  <i className={`${f.icon} text-2xl ${f.color}`}></i>
                </div>
                <h3 className="text-base font-bold mb-1 text-white">{f.title}</h3>
                <p className="text-sm text-zinc-400">{f.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-zinc-900 border-t border-zinc-800 py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="text-base font-semibold mb-4 text-amber-400">Shop</h4>
              <ul className="space-y-2">
                <li><a href="/shop/chips" className="text-sm text-zinc-400 hover:text-amber-400 transition-colors cursor-pointer">Chips Store</a></li>
                <li><a href="/shop/coins" className="text-sm text-zinc-400 hover:text-amber-400 transition-colors cursor-pointer">Gold Coins Store</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-base font-semibold mb-4 text-amber-400">Support</h4>
              <ul className="space-y-2">
                <li><a href="https://t.me/AmagiPoker" target="_blank" rel="noopener noreferrer" className="text-sm text-zinc-400 hover:text-amber-400 transition-colors cursor-pointer">Contact Us</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-base font-semibold mb-4 text-amber-400">Legal</h4>
              <ul className="space-y-2">
                <li><a href="/privacy" className="text-sm text-zinc-400 hover:text-amber-400 transition-colors cursor-pointer">Privacy Policy</a></li>
                <li><a href="/terms" className="text-sm text-zinc-400 hover:text-amber-400 transition-colors cursor-pointer">Terms of Service</a></li>
                <li><a href="/license" className="text-sm text-zinc-400 hover:text-amber-400 transition-colors cursor-pointer">License Agreement</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-base font-semibold mb-4 text-amber-400">Download</h4>
              <div className="flex gap-3">
                <a href="https://apps.apple.com/us/app/amagi-poker-texas-holdem/id6749308770" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center hover:border-amber-400 transition-colors cursor-pointer" title="App Store">
                  <i className="ri-apple-fill text-lg"></i>
                </a>
                <a href="https://play.google.com/store/apps/details?id=com.GAMEINCAMAGI.POKERAGG2D" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center hover:border-amber-400 transition-colors cursor-pointer" title="Google Play">
                  <i className="ri-google-play-fill text-lg"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="pt-8 border-t border-zinc-800 text-center">
            <p className="text-sm text-zinc-500">© 2025 Amagi Poker. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
